import { getGetCurrentUserQueryKey, useUpdateCurrentUserPassword } from "@bvhi/backend-api/apis";
import { Button, LoadingButton, Stack } from "@bvhi/ui-base";
import { UpdateIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, ref, string } from "yup";
import { FormBlockerDialog } from "../../../components/form/form-blocker-dialog";
import { FormWithActionCard } from "../../../components/form/form-with-action-card";
import { useApiErrorContext } from "../../../context/use-api-error-context";
import { DoubleSecuredConfirmDialog } from "../../../dialogs/double-secured-confirm-dialog";
import { useAuthentication } from "../../../hooks/use-authentication";
import { useToast } from "../../../hooks/use-toast";
import { UpdatePasswordCard } from "./components/update-password-card";

interface UpdatePasswordFormValues {
  password: string;
  passwordConfirmation: string;
}

export const UpdatePasswordView = () => {
  const { t } = useTranslation(["validation", "users", "errors", "profile", "common", "dialogs"]);
  const { setErrors } = useApiErrorContext();
  const { logout } = useAuthentication();
  const queryClient = useQueryClient();
  const { pushUpdateSuccessToast, pushUpdateErrorToast } = useToast();
  const { mutate: updateCurrentUserPassword, isPending: isUpdateUserPasswordPending } = useUpdateCurrentUserPassword();

  const [securedUpdateDialogOpen, setSecuredUpdateDialogOpen] = useState<boolean>(false);

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const onClose = useCallback(
    (confirmed: boolean) => {
      setSecuredUpdateDialogOpen(false);
      if (confirmed) primaryButtonRef.current?.click();
    },
    [primaryButtonRef, setSecuredUpdateDialogOpen]
  );

  const updateCurrentUserSchema: AnyObjectSchema = object({
    password: string()
      .min(12, t("validation:string.min.number", { value: 12 }))
      .matches(/^(?=.*[A-Z])/, t("validation:string.min.1.capitalized"))
      .matches(/^(?=.*\d)/, t("validation:string.min.1.digit"))
      .matches(/^(?=.*\W)/, t("validation:string.min.1.special.character"))
      .required(t("validation:required")),
    passwordConfirmation: string()
      .min(12, t("validation:string.min.number", { value: 12 }))
      .matches(/^(?=.*[A-Z])/, t("validation:string.min.1.capitalized"))
      .matches(/^(?=.*\d)/, t("validation:string.min.1.digit"))
      .matches(/^(?=.*\W)/, t("validation:string.min.1.special.character"))
      .oneOf([ref("password")], t("validation:no.match"))
      .required(t("validation:required")),
  });

  const {
    formState: { isDirty, isSubmitSuccessful, ...otherFormState },
    handleSubmit,
    ...otherMethods
  } = useForm<UpdatePasswordFormValues>({
    resolver: yupResolver(updateCurrentUserSchema),
    mode: "all",
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });

  const onSubmit: SubmitHandler<UpdatePasswordFormValues> = (data) => {
    updateCurrentUserPassword(
      { data: { password: data.password } },
      {
        onSuccess: () => {
          pushUpdateSuccessToast(t("users:users.user"));
          logout();
        },
        onError: (error) => {
          if (error.status && [400, 403].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushUpdateErrorToast(t("users:users.user"));
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        },
      }
    );
  };

  return (
    <FormProvider formState={{ isDirty, isSubmitSuccessful, ...otherFormState }} handleSubmit={handleSubmit} {...otherMethods}>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <Stack pt={2} spacing={2}>
          <FormWithActionCard
            contained
            actionCardContent={
              <LoadingButton
                disabled={!isDirty}
                loading={isUpdateUserPasswordPending}
                onClick={() => setSecuredUpdateDialogOpen(true)}
                startIcon={<UpdateIcon />}
              >
                {t("common:form.button.update", { value: t("profile:profile.current.user.password.entity") })}
              </LoadingButton>
            }
          >
            <UpdatePasswordCard />
          </FormWithActionCard>
          <Button ref={primaryButtonRef} sx={{ visibility: "hidden" }} type="submit" />
          <DoubleSecuredConfirmDialog
            onClose={onClose}
            open={securedUpdateDialogOpen}
            overwrite={{
              title: t("dialogs:dialogs.update.password.title"),
              confirmationCode: t("dialogs:dialogs.secured.update.dialog.form.confirm.text.field.placeholer"),
            }}
          />
        </Stack>
        <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
      </form>
    </FormProvider>
  );
};
